type TakeLastFn = (abort: AbortController) => Promise<unknown>;

type TakeLastOptions = {
  delay?: number;
  leading?: boolean;
};

interface ThrottleState {
  abortController: AbortController | null;
  timeoutId: ReturnType<typeof setTimeout> | null;
  lastCallTime: number;
  pendingFn: TakeLastFn | null;
}

const map = new Map<string, ThrottleState>();

export const takeLastByKey = (
  fn: TakeLastFn,
  key: string,
  options?: TakeLastOptions
): Promise<unknown> | undefined => {
  const { delay = 500, leading = true } = options || {};
  const now = Date.now();

  let state = map.get(key);

  if (!state) {
    state = {
      abortController: null,
      timeoutId: null,
      lastCallTime: 0,
      pendingFn: null,
    };
    map.set(key, state);
  }

  if (state.abortController) {
    state.abortController.abort();
    state.abortController = null;
  }

  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
    state.timeoutId = null;
  }

  state.pendingFn = fn;

  const timeSinceLastCall = now - state.lastCallTime;
  const remainingTime = delay - timeSinceLastCall;

  const executeFn = (): Promise<unknown> | undefined => {
    if (!state) {
      return undefined;
    }

    state.lastCallTime = Date.now();
    const abortController = new AbortController();
    state.abortController = abortController;
    state.timeoutId = null;

    const currentFn = state.pendingFn;
    state.pendingFn = null;

    if (currentFn) {
      return currentFn(abortController).finally(() => {
        if (state && state.abortController === abortController) {
          state.abortController = null;
        }
      });
    }

    return undefined;
  };

  if (leading && timeSinceLastCall >= delay) {
    state.lastCallTime = now;
    return executeFn();
  }

  const waitTime = leading ? Math.max(remainingTime, 0) : delay;

  return new Promise((resolve) => {
    if (state) {
      state.timeoutId = setTimeout(() => {
        resolve(executeFn());
      }, waitTime);
    } else {
      resolve(undefined);
    }
  });
};
